import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Wand2, Copy, Download, Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";

const ImproveText = () => {
  const [inputText, setInputText] = useState("");
  const [improvedText, setImprovedText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [formality, setFormality] = useState("formal");
  const [documentType, setDocumentType] = useState("geral");
  const [tone, setTone] = useState("neutro");

  const handleImproveText = async () => {
    if (!inputText.trim()) {
      toast({
        title: "Erro",
        description: "Por favor, insira um texto para melhorar.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const prompt = `
        Melhore o seguinte texto jurídico seguindo estas especificações:
        - Nível de formalidade: ${formality}
        - Tipo de documento: ${documentType}
        - Tom: ${tone}
        
        Texto original:
        ${inputText}
        
        Por favor, retorne apenas o texto melhorado, sem explicações adicionais.
      `;

      const { data, error } = await supabase.functions.invoke('openai-chat', {
        body: {
          messages: [
            {
              role: "system",
              content: "Você é um especialista em redação jurídica brasileira. Sua tarefa é melhorar textos jurídicos mantendo a precisão técnica e adequando-os ao estilo solicitado."
            },
            {
              role: "user",
              content: prompt
            }
          ]
        }
      });

      if (error) throw error;

      if (data?.content || data?.response) {
        setImprovedText(data.content || data.response);
        toast({
          title: "Sucesso",
          description: "Texto melhorado com sucesso!",
        });
      }
    } catch (error) {
      console.error("Erro ao melhorar texto:", error);
      toast({
        title: "Erro",
        description: "Ocorreu um erro ao melhorar o texto. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyText = async () => {
    if (improvedText) {
      await navigator.clipboard.writeText(improvedText);
      toast({
        title: "Copiado",
        description: "Texto copiado para a área de transferência.",
      });
    }
  };

  const handleDownloadText = () => {
    if (improvedText) {
      const element = document.createElement("a");
      const file = new Blob([improvedText], { type: "text/plain" });
      element.href = URL.createObjectURL(file);
      element.download = "texto-melhorado.txt";
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
      
      toast({
        title: "Download iniciado",
        description: "O arquivo foi baixado com sucesso.",
      });
    }
  };

  return (
    <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
          <div className="flex items-center gap-2">
            <Wand2 className="h-5 w-5" />
            <h1 className="font-semibold">Melhore seu Texto</h1>
          </div>
        </header>
        <div className="container max-w-6xl mx-auto p-6 space-y-6">
      <div className="text-center space-y-2">
        <p className="text-muted-foreground">
          Use a inteligência artificial para aprimorar seus textos jurídicos
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Entrada de Texto */}
        <Card>
          <CardHeader>
            <CardTitle>Texto Original</CardTitle>
            <CardDescription>
              Cole ou digite o texto que deseja melhorar
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              placeholder="Cole seu texto aqui para melhorar..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              className="min-h-[300px] resize-none"
              data-tour="input-text-area"
            />

            {/* Opções de Personalização */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-tour="customization-options">
              <div className="space-y-2">
                <Label htmlFor="formality">Formalidade</Label>
                <Select value={formality} onValueChange={setFormality}>
                  <SelectTrigger data-tour="formality-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="informal">Informal</SelectItem>
                    <SelectItem value="formal">Formal</SelectItem>
                    <SelectItem value="muito-formal">Muito Formal</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="documentType">Tipo de Documento</Label>
                <Select value={documentType} onValueChange={setDocumentType}>
                  <SelectTrigger data-tour="document-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="geral">Geral</SelectItem>
                    <SelectItem value="peticao">Petição</SelectItem>
                    <SelectItem value="contrato">Contrato</SelectItem>
                    <SelectItem value="parecer">Parecer</SelectItem>
                    <SelectItem value="oficio">Ofício</SelectItem>
                    <SelectItem value="recurso">Recurso</SelectItem>
                    <SelectItem value="memorial">Memorial</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tone">Tom</Label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger data-tour="tone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="neutro">Neutro</SelectItem>
                    <SelectItem value="persuasivo">Persuasivo</SelectItem>
                    <SelectItem value="tecnico">Técnico</SelectItem>
                    <SelectItem value="didatico">Didático</SelectItem>
                    <SelectItem value="assertivo">Assertivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button 
              onClick={handleImproveText} 
              disabled={isLoading || !inputText.trim()}
              className="w-full"
              data-tour="improve-button"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Melhorando texto...
                </>
              ) : (
                <>
                  <Wand2 className="h-4 w-4 mr-2" />
                  Melhorar Texto
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Resultado */}
        <Card>
          <CardHeader>
            <CardTitle>Texto Melhorado</CardTitle>
            <CardDescription>
              Resultado aprimorado pela inteligência artificial
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={improvedText}
              readOnly
              placeholder="O texto melhorado aparecerá aqui..."
              className="min-h-[300px] resize-none bg-muted/50"
              data-tour="result-area"
            />

            {improvedText && (
              <div className="flex gap-2" data-tour="action-buttons">
                <Button 
                  variant="outline" 
                  onClick={handleCopyText}
                  className="flex-1"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copiar
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleDownloadText}
                  className="flex-1"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Baixar
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Informações sobre o serviço */}
      <Card>
        <CardContent className="pt-6">
          <div className="text-center space-y-2">
            <h3 className="font-semibold">Como funciona?</h3>
            <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
              Nossa IA especializada em textos jurídicos analisa seu conteúdo e o aprimora 
              conforme suas especificações de formalidade, tipo de documento e tom desejado. 
              Ideal para melhorar petições, contratos, pareceres e outros documentos jurídicos.
            </p>
          </div>
        </CardContent>
      </Card>
        </div>
      </SidebarInset>
  );
};

export default ImproveText;